import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Joyride,
  EventData,
  Step,
  STATUS,
  TooltipRenderProps,
} from 'react-joyride';
import { SvgIcon } from '@progress/kendo-react-common';
import { xIcon } from '@progress/kendo-svg-icons';
import styles from './GuidedTour.module.scss';

// Ported from dealerflow360/web/src/shared/components/GuidedTour. Keep the two in sync
// when fixing behaviour; only the icon and the styling tokens differ.

interface GuidedTourProps {
  steps: Step[];
  run: boolean;
  onFinish: () => void;
  tourKey?: number;
}

// Native buttons here on purpose: Joyride hands us prop bags (closeProps, backProps, ...)
// built for plain <button> elements, and the tooltip is rendered inside Joyride's portal
// where Kendo Button chrome fights the tooltip styling.
function Tooltip({
  continuous,
  index,
  step,
  size,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}: TooltipRenderProps) {
  return (
    <div className={styles.tooltip} data-testid="tour-tooltip" {...tooltipProps}>
      <div className={styles.header}>
        {step.title && <div className={styles.title}>{step.title}</div>}
        <button className={styles.closeBtn} {...closeProps} type="button">
          <SvgIcon icon={xIcon} size="small" />
        </button>
      </div>

      <div className={styles.body}>{step.content}</div>

      <div className={styles.footer}>
        <span className={styles.progress}>
          {index + 1} of {size}
        </span>

        <div className={styles.actions}>
          {index > 0 && (
            <button className={styles.skipBtn} {...skipProps} type="button">
              Skip
            </button>
          )}
          {index > 0 && (
            <button className={styles.backBtn} {...backProps} type="button">
              Back
            </button>
          )}
          {(() => {
            const label = isLastStep ? 'Done' : continuous ? 'Next' : 'Got it';
            // Kendo theme classes so the primary action matches the site's other
            // primary buttons (the theme CSS is global, so it reaches this portal).
            const kendoPrimary = 'k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary';
            return (
              <button
                className={`${kendoPrimary} ${styles.nextBtn}`}
                {...primaryProps}
                type="button"
                aria-label={label}
              >
                {label}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/** `document.querySelector` that yields null for a malformed selector instead of throwing. */
function querySafe(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null; // malformed selector — treat as absent, never throw mid-tour
  }
}

/** Every shape Joyride accepts as a target: selector, element, ref, or getter. */
function resolveTarget(target: Step['target']): Element | null {
  if (typeof target === 'string') return querySafe(target);
  if (typeof target === 'function') return target();
  if (target && typeof target === 'object' && 'current' in target) return target.current;
  return target ?? null;
}

/**
 * Is this step's target on the page and visible?
 *
 * A step whose target never appears does NOT fail gracefully: Joyride holds the
 * overlay for `targetWaitTimeout` per step (1s by default) before skipping on, and
 * with `overlayClickAction: false` there is nothing to click in the meantime — a
 * tour whose targets are all absent reads as a hung page. A target that exists but
 * is hidden (a responsive slot switched off at this width) does the same.
 *
 * Deliberately NOT `getClientRects()`: jsdom performs no layout and returns an
 * empty list for every element, so that check would report every target missing
 * under test — silently disabling all tours in exactly the environment meant to
 * catch that. Existence plus computed visibility behaves identically in both.
 */
function isTargetRenderable(step: Step): boolean {
  const element = resolveTarget(step.target);
  if (!(element instanceof Element) || !element.isConnected) return false;
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

export function GuidedTour({ steps, run, onFinish, tourKey }: GuidedTourProps) {
  const handleCallback = useCallback(
    (data: EventData) => {
      const { status } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        onFinish();
      }
    },
    [onFinish],
  );

  // Sampled when a tour starts, not per render: every launch passes a fresh `steps`
  // array (and flips `run`), and pinning the set here keeps a step from vanishing
  // mid-tour if its target unmounts.
  const runnableSteps = useMemo(
    () => (run ? steps.filter(isTargetRenderable) : steps),
    [run, steps],
  );

  const nothingToShow = run && runnableSteps.length === 0;

  // Tell the caller the tour is over, so its `run` flag doesn't stay latched on.
  useEffect(() => {
    if (nothingToShow) onFinish();
  }, [nothingToShow, onFinish]);

  // Nothing to point at: stay unmounted rather than covering the page with an
  // overlay that has no tooltip and no way out.
  if (nothingToShow) return null;

  return (
    <Joyride
      key={tourKey}
      steps={runnableSteps}
      run={run}
      onEvent={handleCallback}
      continuous
      scrollToFirstStep
      tooltipComponent={Tooltip}
      options={{
        zIndex: 10000,
        arrowColor: '#ffffff',
        overlayColor: 'rgba(0, 0, 0, 0.45)',
        showProgress: true,
        skipBeacon: true,
        overlayClickAction: false,
        buttons: ['back', 'close', 'primary', 'skip'],
        // The X reads as "leave this tour", so make it do that. Joyride's default
        // ('close') ADVANCES to the next step instead, and Skip only renders past
        // step 1 — leaving the first step of a tour with no way out but stepping
        // through it, on top of an overlay that swallows clicks.
        closeButtonAction: 'skip',
        // Never hold the overlay waiting for a target. The pre-filter above catches
        // what it can see; this covers what it can't (a ref target, or one that
        // unmounts mid-tour), which would otherwise freeze the overlay for the
        // 1s default per step with no tooltip on screen.
        targetWaitTimeout: 0,
      }}
    />
  );
}
